import { fmtInt, t } from '../i18n/i18n';
import { useMemo, useState } from 'react';
import { FORMATS, FORMAT_IDS, type FormatId, safeFileName } from '../formats';
import { store, useApp } from '../state/store';
import type { ExportResponse } from '../workers/protocol';
import { downloadBlob } from './actions';
import { IconClose, IconExport } from './icons';
import { IssueList, uniqueIssues } from './Issues';

const COLOR_INFO = { none: 'No colours (colour changes only)', palette: 'Nearest colour in the machine palette', rgb: 'Exact RGB colours' };
const TRIM_INFO = { native: 'Own trim command', 'jump-sequence': 'Jump sequence (Tajima convention)', 'machine-setting': "By the machine's jump setting" };

export function ExportDialog() {
  const current = useApp((s) => s.history.present);
  const projectName = useApp((s) => s.project.name);
  // the name in the top bar is the design name: it names the file and the label inside it
  const design = useMemo(() => ({ ...current, name: projectName.trim() || current.name }), [current, projectName]);
  const stitch = useApp((s) => s.stitch);
  const [format, setFormat] = useState<FormatId>(() => (localStorageGet('stitchcraft.format') as FormatId) ?? 'dst');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<ExportResponse | null>(null);
  const close = () => store.set({ panel: null });
  const errors = stitch.result?.issues.filter((i) => i.severity === 'error') ?? [];
  const hidden = design.objects.filter((o) => !o.visible);
  const warnings = uniqueIssues(stitch.result?.issues.filter((i) => i.severity === 'warning') ?? []);

  const run = async () => {
    setBusy(true);
    setRes(null);
    localStorageSet('stitchcraft.format', format);
    try {
      const r = await store.getClient().export(design, format);
      setRes(r);
      if (r.ok && r.bytes) downloadBlob(new Blob([r.bytes as BlobPart], { type: 'application/octet-stream' }), r.fileName);
    } catch (e) {
      setRes({ ok: false, bytes: null, fileName: '', issues: [{ severity: 'error', code: 'export', message: e instanceof Error ? e.message : String(e) }], readBack: null, formatName: FORMATS[format].name });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="export-title" onKeyDown={(e) => e.key === 'Escape' && close()}>
      <div className="modal narrow">
        <div className="modal-head">
          <h2 id="export-title">{t('Export embroidery file')}</h2>
          <button className="btn icon ghost" aria-label={t('Close dialog')} onClick={close}>
            <IconClose />
          </button>
        </div>
        <div className="modal-body">
          <div className="format-list" role="radiogroup" aria-label={t('File format')}>
            {FORMAT_IDS.map((id) => {
              const f = FORMATS[id];
              return (
                <div
                  key={id}
                  className="format-card"
                  role="radio"
                  aria-checked={format === id}
                  tabIndex={0}
                  onClick={() => {
                    setFormat(id);
                    setRes(null);
                  }}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setFormat(id)}
                >
                  <span className="ext">.{f.extension}</span>
                  <span>
                    <b>{f.name}</b> <small>· {t(f.machines)}</small>
                  </span>
                  <small>
                    {t(COLOR_INFO[f.colors])} · {t('Trims')}: {t(TRIM_INFO[f.trims])}
                  </small>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12 }}>
            <IssueList issues={FORMATS[format].notes.map((message) => ({ severity: 'info' as const, code: 'note', message: t(message) }))} onSelect={false} />
          </div>
          {hidden.length > 0 && (
            <p className="issue warning" style={{ marginTop: 12 }}>
              {(() => {
                const params = { n: hidden.length, names: hidden.map((o) => o.name).slice(0, 4).join(', ') + (hidden.length > 4 ? ' …' : '') };
                return hidden.length === 1
                  ? t('{n} hidden object ({names}) is not in the file. Show it in the object list if it should be sewn.', params)
                  : t('{n} hidden objects ({names}) are not in the file. Show them in the object list if they should be sewn.', params);
              })()}
            </p>
          )}
          <p className="hint">
            {t('File name')}: <b>{safeFileName(design.name)}.{FORMATS[format].extension}</b> · {t('Change the name in the top bar.')}
          </p>
          {stitch.pending && <p className="hint">{t('Updating the stitches …')}</p>}
          {errors.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <p className="hint" style={{ color: 'var(--err)' }}>
                {t('The design has errors that must be fixed before export:')}
              </p>
              <IssueList issues={uniqueIssues(errors)} />
            </div>
          )}
          {warnings.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <IssueList issues={warnings} />
            </div>
          )}
          {res && (
            <div style={{ marginTop: 14 }} aria-live="polite">
              {res.ok && res.readBack ? (
                <div className="issue" style={{ borderLeftColor: 'var(--ok)', display: 'block' }}>
                  <b>{res.fileName}</b> {t('was written, read back and checked against the internal stitch sequence.')}
                  <dl className="kv" style={{ marginTop: 8 }}>
                    <dt>{t('Stitches')}</dt>
                    <dd>{fmtInt(res.readBack.stitches)}</dd>
                    <dt>{t('Jump records')}</dt>
                    <dd>{res.readBack.jumps}</dd>
                    <dt>{t('Trims')}</dt>
                    <dd>{res.readBack.trims}</dd>
                    <dt>{t('Colour changes')}</dt>
                    <dd>{res.readBack.colorChanges}</dd>
                    <dt>{t('Size')}</dt>
                    <dd>
                      {res.readBack.widthMm.toFixed(1)} × {res.readBack.heightMm.toFixed(1)} mm
                    </dd>
                    <dt>{t('File size')}</dt>
                    <dd>{((res.bytes?.length ?? 0) / 1024).toFixed(1)} kB</dd>
                  </dl>
                </div>
              ) : (
                <IssueList issues={res.issues.filter((i) => i.severity === 'error')} />
              )}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={close}>
            {t('Close')}
          </button>
          <button className="btn primary" onClick={run} disabled={busy || errors.length > 0 || !stitch.result || stitch.result.stats.stitches === 0}>
            <IconExport size={14} /> {busy ? t('Checking…') : t('Validate and download .{ext}', { ext: FORMATS[format].extension })}
          </button>
        </div>
      </div>
    </div>
  );
}

function localStorageGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function localStorageSet(k: string, v: string): void {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* storage unavailable (private mode); preference is not essential */
  }
}
